import React, { FC } from 'react'
import styles from './PolaroidList.module.scss'
import Polaroid, { PolaroidSpecificPageProps } from './Polaroid'
import { SAArtwork } from 'types/Artwork'
import { PolaroidCompleteSkeleton } from './components/PolaroidSkeleton'
import classNames from 'classnames'
import useQA from 'hooks/useQA'

type PolaroidListProps = PolaroidSpecificPageProps & {
  artworks: ReadonlyArray<SAArtwork>
  isLoading?: boolean
  isArtistNameVisible?: boolean
  isPrintsFromVisible?: boolean
  skeletonQuantity?: number
  userID?: number
  isUsersArtworksAccount?: boolean
  customStyle?: string
  isMediumMaterialVisible?: boolean
  isDimensionsVisible?: boolean
  customPriceElement?: (artwork: SAArtwork) => JSX.Element
  customPromoCode?: string
  showPrintsFromInPrice?: boolean
  showOriginalPrice?: boolean
  hasPrints?: boolean
  onCheckIsPrintForDataLayerEvent?: (artwork: SAArtwork) => boolean
}

const PolaroidList: FC<PolaroidListProps> = ({
  artworks,
  algoliaIndice,
  algoliaUserToken,
  dataApi,
  dataList,
  dataListId,
  dataListName,
  isLoading = false,
  skeletonQuantity = 6,
  isArtistNameVisible = true,
  isPrintsFromVisible = true,
  userID,
  isMediumMaterialVisible,
  isDimensionsVisible,
  isUsersArtworksAccount,
  customStyle = '',
  customPriceElement,
  customPromoCode,
  showPrintsFromInPrice,
  showOriginalPrice,
  hasPrints,
  onCheckIsPrintForDataLayerEvent,
}) => {
  const { testId } = useQA()
  if (isLoading) {
    return (
      <div
        className={classNames(styles.polaroidListContainer, {
          [customStyle]: customStyle !== '',
        })}
        {...testId('polaroid-list-container-loading')}
      >
        {Array.from({ length: skeletonQuantity }, (_, index) => (
          <PolaroidCompleteSkeleton key={index.toString()} />
        ))}
      </div>
    )
  }

  if (!artworks?.length) {
    return null
  }

  return (
    <div
      className={classNames(styles.polaroidListContainer, {
        [customStyle]: customStyle !== '',
      })}
      {...testId('polaroid-list-container')}
    >
      {artworks.map((artwork, index) => {
        const priority = index === 0
        const lazyLoad = index > 3
        const isImageSrcFromProd = index < 6
        return (
          <Polaroid
            key={artwork.artworkId}
            artwork={artwork}
            algoliaIndice={algoliaIndice}
            algoliaUserToken={algoliaUserToken}
            dataApi={dataApi}
            dataList={dataList}
            dataListId={dataListId}
            dataListName={dataListName}
            isArtistNameVisible={isArtistNameVisible}
            isPrintsFromVisible={isPrintsFromVisible}
            priority={priority}
            lazyLoad={lazyLoad}
            isImageSrcFromProd={isImageSrcFromProd}
            userID={userID}
            isUsersArtworksAccount={isUsersArtworksAccount}
            isMediumMaterialVisible={isMediumMaterialVisible}
            isDimensionsVisible={isDimensionsVisible}
            customPriceElement={!!customPriceElement ? customPriceElement(artwork) : undefined}
            customPromoCode={!!customPromoCode ? customPromoCode : ''}
            showPrintsFromInPrice={showPrintsFromInPrice}
            showOriginalPrice={showOriginalPrice}
            hasPrints={hasPrints}
            isPrintForDataLayerEvent={
              !!onCheckIsPrintForDataLayerEvent ? onCheckIsPrintForDataLayerEvent(artwork) : false
            }
          />
        )
      })}
    </div>
  )
}

export default PolaroidList
